class ShortVideoGallery extends HTMLElement {
  connectedCallback() {
    this.scroller = this.querySelector('.short-video-gallery__scroller');
    this.modal = this.querySelector('.short-video-gallery__modal');
    this.modalScroller = this.querySelector('.short-video-gallery__modal-scroller');
    this.closeBtn = this.querySelector('.short-video-gallery__close');
    this.muteBtn = this.querySelector('.short-video-gallery__mute-toggle');
    this.muted = true;
    this.activeItem = null;

    this.scroller.querySelectorAll('.short-video-gallery__item').forEach((item, index) => {
      item.addEventListener('click', () => this.openModal(index));
    });

    this.closeBtn.addEventListener('click', () => this.closeModal());
    this.muteBtn.addEventListener('click', () => this.toggleMute());

    this.modalScroller.querySelectorAll('.short-video-gallery__modal-item').forEach((item) => {
      item.addEventListener('click', () => this.togglePlay(item));
    });

    this.modalScroller.addEventListener('touchstart', (event) => {
      this.touchStartY = event.touches[0].clientY;
    });

    this.modalScroller.addEventListener('touchend', (event) => {
      this.handleSwipeWrap(event.changedTouches[0].clientY);
    });

    // Preload videos slightly before they're fully in view to cut perceived lag.
    this.preloadObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.preparePlayer(entry.target);
        });
      },
      { root: this.modalScroller, rootMargin: '0px 0px 150% 0px' }
    );

    this.activateObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.activateItem(entry.target);
          } else {
            this.deactivateItem(entry.target);
          }
        });
      },
      { root: this.modalScroller, threshold: 0.7 }
    );
  }

  openModal(index) {
    this.modal.hidden = false;
    document.body.style.overflow = 'hidden';

    const items = this.modalScroller.querySelectorAll('.short-video-gallery__modal-item');
    items.forEach((item) => {
      this.preloadObserver.observe(item);
      this.activateObserver.observe(item);
    });

    const target = items[index];
    target.scrollIntoView({ block: 'start' });
    this.preparePlayer(target);
    this.activateItem(target);
  }

  closeModal() {
    this.modal.hidden = true;
    document.body.style.overflow = '';
    this.modalScroller
      .querySelectorAll('.short-video-gallery__modal-item')
      .forEach((item) => this.deactivateItem(item));
    this.preloadObserver.disconnect();
    this.activateObserver.disconnect();
    this.activeItem = null;
  }

  toggleMute() {
    this.muted = !this.muted;
    this.muteBtn.textContent = this.muted ? '🔇' : '🔊';

    if (!this.activeItem) return;

    const mediaType = this.activeItem.dataset.mediaType;
    const player = this.activeItem.querySelector('.short-video-gallery__modal-player');

    if (mediaType === 'video') {
      const video = player.querySelector('video');
      if (video) video.muted = this.muted;
      return;
    }

    this.postCommand(player.querySelector('iframe'), this.activeItem.dataset.host, this.muted ? 'mute' : 'unMute');
  }

  handleSwipeWrap(touchEndY) {
    if (this.touchStartY === undefined) return;

    const deltaY = this.touchStartY - touchEndY; // positive = swiped up (toward next)
    const SWIPE_THRESHOLD = 40;
    const scroller = this.modalScroller;

    // Wait briefly so the native scroll-snap settle finishes before reading scrollTop.
    setTimeout(() => {
      const atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 10;
      const atTop = scroller.scrollTop <= 10;
      const items = this.modalScroller.querySelectorAll('.short-video-gallery__modal-item');

      if (deltaY > SWIPE_THRESHOLD && atBottom) {
        items[0].scrollIntoView({ block: 'start' });
      } else if (deltaY < -SWIPE_THRESHOLD && atTop) {
        items[items.length - 1].scrollIntoView({ block: 'start' });
      }
    }, 80);
  }

  togglePlay(item) {
    const mediaType = item.dataset.mediaType;
    const player = item.querySelector('.short-video-gallery__modal-player');
    const paused = item.dataset.paused === 'true';

    if (mediaType === 'video') {
      const video = player.querySelector('video');
      if (!video) return;
      paused ? video.play().catch(() => {}) : video.pause();
    } else {
      const iframe = player.querySelector('iframe');
      this.postCommand(iframe, item.dataset.host, paused ? 'play' : 'pause');
    }

    item.dataset.paused = paused ? 'false' : 'true';
    this.flashIcon(item, paused ? '▶' : '❚❚');
  }

  flashIcon(item, symbol) {
    let icon = item.querySelector('.short-video-gallery__tap-icon');
    if (!icon) {
      icon = document.createElement('span');
      icon.className = 'short-video-gallery__tap-icon';
      item.appendChild(icon);
    }
    icon.textContent = symbol;
    icon.classList.remove('is-visible');
    // restart animation
    void icon.offsetWidth;
    icon.classList.add('is-visible');
  }

  // Creates the player element (iframe/video) ahead of time so it's ready when scrolled into view.
  preparePlayer(item) {
    const player = item.querySelector('.short-video-gallery__modal-player');
    const mediaType = item.dataset.mediaType;

    if (mediaType === 'video' || player.dataset.loaded) return;

    player.dataset.loaded = 'true';
    const host = item.dataset.host;
    const videoId = item.dataset.videoId;
    const mute = this.muted ? 1 : 0;
    const iframe = document.createElement('iframe');
    iframe.allow = 'autoplay; encrypted-media; fullscreen';
    iframe.setAttribute('frameborder', '0');

    if (host === 'youtube') {
      iframe.src = `https://www.youtube.com/embed/${videoId}?enablejsapi=1&autoplay=0&mute=${mute}&loop=1&playlist=${videoId}&playsinline=1&rel=0`;
    } else if (host === 'vimeo') {
      iframe.src = `https://player.vimeo.com/video/${videoId}?autoplay=0&muted=${mute}&loop=1`;
    }

    player.appendChild(iframe);
  }

  activateItem(item) {
    this.activeItem = item;
    item.dataset.paused = 'false';
    this.preparePlayer(item);

    const mediaType = item.dataset.mediaType;
    const player = item.querySelector('.short-video-gallery__modal-player');

    if (mediaType === 'video') {
      const video = player.querySelector('video');
      if (video) {
        video.muted = this.muted;
        video.currentTime = 0;
        video.play().catch(() => {});
      }
      return;
    }

    const iframe = player.querySelector('iframe');
    this.postCommand(iframe, item.dataset.host, 'play');
  }

  deactivateItem(item) {
    item.dataset.paused = 'true';
    const mediaType = item.dataset.mediaType;
    const player = item.querySelector('.short-video-gallery__modal-player');

    if (mediaType === 'video') {
      const video = player.querySelector('video');
      if (video) video.pause();
      return;
    }

    const iframe = player.querySelector('iframe');
    this.postCommand(iframe, item.dataset.host, 'pause');
  }

  postCommand(iframe, host, action) {
    if (!iframe || !iframe.contentWindow) return;

    if (host === 'youtube') {
      const funcMap = { play: 'playVideo', pause: 'pauseVideo', mute: 'mute', unMute: 'unMute' };
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: funcMap[action], args: [] }),
        '*'
      );
    } else if (host === 'vimeo') {
      const methodMap = { play: 'play', pause: 'pause', mute: 'setVolume', unMute: 'setVolume' };
      const value = action === 'mute' ? 0 : action === 'unMute' ? 1 : undefined;
      const payload = { method: methodMap[action] };
      if (value !== undefined) payload.value = value;
      iframe.contentWindow.postMessage(JSON.stringify(payload), '*');
    }
  }
}

customElements.define('short-video-gallery', ShortVideoGallery);